import { M2_TO_SQ_FT, SQ_FT_TO_M2 } from '@util/constants';
import { getCurrentLocale } from '@util/locale';
import { reaction } from 'mobx';
import { MeasurementUnit } from 'mobx/stores/app-store';
import { useStores } from 'mobx/useStores';
import { useCallback, useEffect, useState } from 'react';

export type ConvertAreaFn = (
  val: number,
  baseUnit: MeasurementUnit,
) => { formattedArea: string; rawArea: number };

interface MeasurementHook {
  convertArea: ConvertAreaFn;
  preferredMeasurement: string | null;
}

export const useMeasurement = (): MeasurementHook => {
  const locale = getCurrentLocale();
  const { appStore } = useStores();
  const [preferredMeasurement, setPreferredMeasurement] = useState<string | null>(
    appStore.preferredMeasurement,
  );

  // Listen for changes in the store here because we can't use the observer HOC in a hook
  useEffect(() => {
    const disposers: (() => void)[] = [];

    disposers.push(
      reaction(
        () => appStore.preferredMeasurement,
        (val) => setPreferredMeasurement(val),
      ),
    );

    return () => {
      disposers.forEach((disposer) => disposer());
    };
  }, []);

  /** Converts a value to another measurement */
  const convertArea: ConvertAreaFn = useCallback(
    (val, baseUnit) => {
      let area: number | undefined;
      let targetUnit: MeasurementUnit | undefined;
      if (baseUnit === 'sq.ft' && preferredMeasurement === 'm²') {
        area = val * SQ_FT_TO_M2;
        targetUnit = 'm²';
      } else if (baseUnit === 'm²' && preferredMeasurement === 'sq.ft') {
        area = val * M2_TO_SQ_FT;
        targetUnit = 'sq.ft';
      } else {
        area = val;
        targetUnit = baseUnit;
      }

      const lang = locale ?? 'en-US';
      const formattedArea = new Intl.NumberFormat(lang).format(Math.round(area));
      return { formattedArea: `${formattedArea} ${targetUnit}`, rawArea: area! };
    },
    [preferredMeasurement],
  );

  return {
    convertArea,
    preferredMeasurement,
  };
};
